import {
	cn,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import { parsePriceToNumber } from "../../../components/inputs/PriceInput";
import { ControlledPriceInput } from "../../../components/inputs/ControlledPriceInput";
import { RateInput } from "../../../components/inputs/RateInput";
import {
	doPricingFieldKeys,
	mrtPricingFieldKeys,
	trcPricingFieldKeys,
	pricingFieldLabelMap,
	pjPricingFieldKeys,
} from "@dune/vars";
import type { OffersWarrantyType, PricingDetailsFormValues } from "@dune/types";
import { formatPricing } from "@dune/utils";

export const calculateTotal = (
	watchedFormValues: PricingDetailsFormValues,
	warrantyType: OffersWarrantyType,
) => {
	const convertPriceStringToNumber = (value?: string) => {
		const parsed = parsePriceToNumber(value ?? undefined);
		return parsed ?? 0;
	};

	let totalAmountExcludingTaxes = 0;
	let totalAmountTaxes = 0;
	let totalAmountIncludingTaxes = 0;

	if (warrantyType === "DO") {
		totalAmountExcludingTaxes +=
			convertPriceStringToNumber(watchedFormValues.doPrimeHT) +
			convertPriceStringToNumber(watchedFormValues.existingDamagesPrimeHT) +
			convertPriceStringToNumber(watchedFormValues.cnrPrimeHT) +
			convertPriceStringToNumber(
				watchedFormValues.terrorismContributionPrimeHT,
			);
		totalAmountTaxes +=
			convertPriceStringToNumber(watchedFormValues.doPrimeTaxes) +
			convertPriceStringToNumber(watchedFormValues.existingDamagesPrimeTaxes) +
			convertPriceStringToNumber(watchedFormValues.cnrPrimeTaxes) +
			convertPriceStringToNumber(
				watchedFormValues.terrorismContributionPrimeTaxes,
			);
		totalAmountIncludingTaxes +=
			convertPriceStringToNumber(watchedFormValues.doPrimeTTC) +
			convertPriceStringToNumber(watchedFormValues.existingDamagesPrimeTTC) +
			convertPriceStringToNumber(watchedFormValues.cnrPrimeTTC) +
			convertPriceStringToNumber(
				watchedFormValues.terrorismContributionPrimeTTC,
			);
	}

	if (warrantyType === "MRT") {
		totalAmountExcludingTaxes +=
			convertPriceStringToNumber(watchedFormValues.trcPrimeHT) +
			convertPriceStringToNumber(watchedFormValues.trcNaturalDisastersPrimeHT) +
			convertPriceStringToNumber(
				watchedFormValues.trcTerrorismContributionPrimeHT,
			) +
			convertPriceStringToNumber(watchedFormValues.rcmoPrimeHT) +
			convertPriceStringToNumber(watchedFormValues.doCnrPrimeHT);
		totalAmountTaxes +=
			convertPriceStringToNumber(watchedFormValues.trcPrimeTaxes) +
			convertPriceStringToNumber(
				watchedFormValues.trcNaturalDisastersPrimeTaxes,
			) +
			convertPriceStringToNumber(
				watchedFormValues.trcTerrorismContributionPrimeTaxes,
			) +
			convertPriceStringToNumber(watchedFormValues.rcmoPrimeTaxes) +
			convertPriceStringToNumber(watchedFormValues.doCnrPrimeTaxes);
		totalAmountIncludingTaxes +=
			convertPriceStringToNumber(watchedFormValues.trcPrimeTTC) +
			convertPriceStringToNumber(
				watchedFormValues.trcNaturalDisastersPrimeTTC,
			) +
			convertPriceStringToNumber(
				watchedFormValues.trcTerrorismContributionPrimeTTC,
			) +
			convertPriceStringToNumber(watchedFormValues.rcmoPrimeTTC) +
			convertPriceStringToNumber(watchedFormValues.doCnrPrimeTTC);
	}

	if (warrantyType === "TRC") {
		totalAmountExcludingTaxes +=
			convertPriceStringToNumber(watchedFormValues.damageCoveragePrimeHT) +
			convertPriceStringToNumber(watchedFormValues.damageCoverageFirePrimeHT) +
			convertPriceStringToNumber(
				watchedFormValues.damageCoverageOtherDamagesPrimeHT,
			) +
			convertPriceStringToNumber(
				watchedFormValues.damageCoverageNaturalDisastersPrimeHT,
			) +
			convertPriceStringToNumber(watchedFormValues.rcmoPrimeHT) +
			convertPriceStringToNumber(
				watchedFormValues.terrorismContributionPrimeHT,
			);
		totalAmountTaxes +=
			convertPriceStringToNumber(watchedFormValues.damageCoveragePrimeTaxes) +
			convertPriceStringToNumber(
				watchedFormValues.damageCoverageFirePrimeTaxes,
			) +
			convertPriceStringToNumber(
				watchedFormValues.damageCoverageOtherDamagesPrimeTaxes,
			) +
			convertPriceStringToNumber(
				watchedFormValues.damageCoverageNaturalDisastersPrimeTaxes,
			) +
			convertPriceStringToNumber(watchedFormValues.rcmoPrimeTaxes) +
			convertPriceStringToNumber(
				watchedFormValues.terrorismContributionPrimeTaxes,
			);
		totalAmountIncludingTaxes +=
			convertPriceStringToNumber(watchedFormValues.damageCoveragePrimeTTC) +
			convertPriceStringToNumber(watchedFormValues.damageCoverageFirePrimeTTC) +
			convertPriceStringToNumber(
				watchedFormValues.damageCoverageOtherDamagesPrimeTTC,
			) +
			convertPriceStringToNumber(
				watchedFormValues.damageCoverageNaturalDisastersPrimeTTC,
			) +
			convertPriceStringToNumber(watchedFormValues.rcmoPrimeTTC) +
			convertPriceStringToNumber(
				watchedFormValues.terrorismContributionPrimeTTC,
			);
	}

	if (warrantyType === "PJ") {
		totalAmountExcludingTaxes += convertPriceStringToNumber(
			watchedFormValues.legalProtectionPrimeHT,
		);
		totalAmountTaxes += convertPriceStringToNumber(
			watchedFormValues.legalProtectionPrimeTaxes,
		);
		totalAmountIncludingTaxes += convertPriceStringToNumber(
			watchedFormValues.legalProtectionPrimeTTC,
		);
	}

	totalAmountExcludingTaxes +=
		convertPriceStringToNumber(watchedFormValues.duneManagementFeesHT) +
		convertPriceStringToNumber(watchedFormValues.intermediaryManagementFeesHT);
	totalAmountTaxes +=
		convertPriceStringToNumber(watchedFormValues.duneManagementFeesTaxes) +
		convertPriceStringToNumber(
			watchedFormValues.intermediaryManagementFeesTaxes,
		);
	totalAmountIncludingTaxes +=
		convertPriceStringToNumber(watchedFormValues.duneManagementFeesTTC) +
		convertPriceStringToNumber(watchedFormValues.intermediaryManagementFeesTTC);

	return {
		totalClientPrimeHT: totalAmountExcludingTaxes,
		totalClientPrimeTaxes: totalAmountTaxes,
		totalClientPrimeTTC: totalAmountIncludingTaxes,
	};
};

type PricingDetailsFormProps = {
	warrantyType: OffersWarrantyType;
};

const Section = ({
	children,
	title,
	className,
}: { children: React.ReactNode; title: string; className?: string }) => (
	<div className={cn("space-y-4", className)}>
		<h3 className="text-lg font-semibold">{title}</h3>
		{children}
	</div>
);

export const PricingDetailsForm = ({
	warrantyType,
}: PricingDetailsFormProps) => {
	const form = useFormContext<PricingDetailsFormValues>();

	const watchedFormValues = form.watch();

	const { totalClientPrimeHT, totalClientPrimeTaxes, totalClientPrimeTTC } =
		useMemo(
			() => calculateTotal(watchedFormValues, warrantyType),
			[watchedFormValues, warrantyType],
		);

	const ManagementFees = (
		<Section title="Frais de gestion">
			<div className="grid grid-cols-3 gap-4">
				<ControlledPriceInput
					control={form.control}
					name="duneManagementFeesHT"
					label="Frais DUNE HT"
				/>
				<ControlledPriceInput
					control={form.control}
					name="duneManagementFeesTaxes"
					label="Frais DUNE Taxes"
				/>
				<ControlledPriceInput
					control={form.control}
					name="duneManagementFeesTTC"
					label="Frais DUNE TTC"
				/>
			</div>
			<div className="grid grid-cols-3 gap-4">
				<ControlledPriceInput
					control={form.control}
					name="intermediaryManagementFeesHT"
					label="Frais apporteur HT"
				/>
				<ControlledPriceInput
					control={form.control}
					name="intermediaryManagementFeesTaxes"
					label="Frais apporteur Taxes"
				/>
				<ControlledPriceInput
					control={form.control}
					name="intermediaryManagementFeesTTC"
					label="Frais apporteur TTC"
				/>
			</div>
		</Section>
	);

	const Commissions = (
		<Section title="Commissions">
			<div className="grid grid-cols-3 gap-4">
				<ControlledPriceInput
					control={form.control}
					name="brokerCommission"
					label="Commission Apporteur"
				/>
				<ControlledPriceInput
					control={form.control}
					name="brokerRemuneration"
					label="Rémunération Apporteur"
				/>
			</div>
		</Section>
	);

	const RecapSection = (
		<Section title="Récapitulatif" className="bg-gray-50 p-4 rounded-lg">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Total HT</TableHead>
						<TableHead>Total Taxes</TableHead>
						<TableHead>Total TTC</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<TableRow>
						<TableCell className="font-bold">
							{formatPricing(totalClientPrimeHT)}
						</TableCell>
						<TableCell className="font-bold">
							{formatPricing(totalClientPrimeTaxes)}
						</TableCell>
						<TableCell className="font-bold">
							{formatPricing(totalClientPrimeTTC)}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</Section>
	);

	const AgreedRates = (
		<Section title="Taux convenus">
			<div className="grid grid-cols-2 gap-4">
				<FormField
					control={form.control}
					name="agreedRateHT"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Taux HT (%)</FormLabel>
							<FormControl>
								<RateInput {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="agreedRateTTC"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Taux TTC (%)</FormLabel>
							<FormControl>
								<RateInput {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
		</Section>
	);

	const DoFields = (
		<Section title="Détails DO">
			<div className="grid grid-cols-3 gap-4">
				{doPricingFieldKeys.map((name) => (
					<ControlledPriceInput
						key={name}
						control={form.control}
						name={name}
						label={pricingFieldLabelMap[name] ?? name}
					/>
				))}
			</div>
		</Section>
	);

	const MrtFields = (
		<Section title="Détails MRT">
			<div className="grid grid-cols-3 gap-4">
				{mrtPricingFieldKeys.map((name) => (
					<ControlledPriceInput
						key={name}
						control={form.control}
						name={name}
						label={pricingFieldLabelMap[name] ?? name}
					/>
				))}
			</div>
		</Section>
	);

	const TrcFields = (
		<Section title="Détails TRC">
			<div className="grid grid-cols-3 gap-4">
				{trcPricingFieldKeys.map((name) => (
					<ControlledPriceInput
						key={name}
						control={form.control}
						name={name}
						label={pricingFieldLabelMap[name] ?? name}
					/>
				))}
			</div>
		</Section>
	);

	const PjFields = (
		<Section title="Détails PJ">
			<div className="grid grid-cols-3 gap-4">
				{pjPricingFieldKeys.map((name) => (
					<ControlledPriceInput
						key={name}
						control={form.control}
						name={name}
						label={pricingFieldLabelMap[name] ?? name}
					/>
				))}
			</div>
		</Section>
	);

	return (
		<>
			{warrantyType === "DO" && DoFields}
			{warrantyType === "MRT" && MrtFields}
			{warrantyType === "TRC" && TrcFields}
			{warrantyType === "PJ" && PjFields}
			{ManagementFees}
			{Commissions}
			{RecapSection}
			{AgreedRates}
		</>
	);
};
